import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import FormSignatureSections from "../FormSignatureSections";
import useFormSignatures from "../useFormSignatures";
import {
  FORM_FLAG_MESSAGES,
  CheckboxGrid,
  ExclusiveChoiceGrid,
  Field,
  FormActionBar,
  FormIdentityStrip,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  LockedReasonBanner,
  OfficeCommentsNote,
  OfficeReviewSection,
  SHEET,
  Section,
  SubHeading,
  checkboxClass,
  fieldClass,
  textareaClass,
} from "../formLayout";
import {
  createPatientClinicalForm,
  getPtVisitNoteForm,
  updatePtVisitNoteForm,
} from "../../../../../services/patientService";
import {
  CommentsIcon,
  DeleteIcon,
  FileCheckIcon,
  HeartPulseIcon,
  HelpingIcon,
  PlusIcon,
  RunningIcon,
  TargetIcon,
} from "../../../../../Icons";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../../patientPageLayout";
import { saveDedicatedForm } from "../clinicalFormDraft";
import {
  draftCaregiverIdentity,
  useFormCaregiverPicker,
} from "../formCaregiver";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";
import { useClinicalCosignPermissions } from "../hooks/useClinicalCosignPermissions";
import {
  applyDisciplineFormPermissions,
  resolveFormActorRole,
  PT_FORM_EDITOR_ROLES,
} from "../disciplineFormAccess";
import {
  ADL_IMPACTED_OPTIONS,
  AMBULATION_STATUS_OPTIONS,
  ASSISTIVE_DEVICE_OPTIONS,
  BALANCE_OPTIONS,
  GAIT_MOBILITY_OPTIONS,
  HOME_SAFETY_OPTIONS,
  MMT_SIDE_OPTIONS,
  MOBILITY_TREND_OPTIONS,
  PATIENT_RESPONSE_OPTIONS,
  PROGRESS_TOWARD_GOALS_OPTIONS,
  PT_VISIT_ARRAY_KEYS,
  PT_VISIT_INTERVENTION_OPTIONS,
  REASON_FOR_VISIT_OPTIONS,
  TRANSFER_STATUS_OPTIONS,
  YES_NO_OPTIONS,
  YES_NO_SOME_OPTIONS,
  emptyMmtRow,
  emptyMmtRows,
  emptyPtVisitPayload,
  toggleInList,
} from "./ptVisitNoteContent";
import {
  emptyTextGoalRow,
  normalizeTextGoals,
  renumberTextGoals,
  serializeTextGoals,
} from "./ptGoals";
import {
  assertPtVisitMetaForFlag,
  buildPtVisitMetaProps,
  pickPtVisitMeta,
} from "./ptVisitCommon";
import PtNotesCommonHeader from "./PtNotesCommonHeader";
import PainScalePills from "./PainScalePills";
import {
  normalizePtCommonHeaderState,
  resolvePtNotesCommonHeader,
  withNormalizedPtCommonHeader,
} from "./ptNotesCommonHeaderUtils";

const VISIT_DATE_MAX = new Date().toISOString().slice(0, 10);
const today = () => new Date().toISOString().slice(0, 10);

const emptyValues = () => ({
  ...emptyPtVisitPayload(),
  visitDate: today(),
  clinicianSignatureName: "",
  patientSignatureName: "",
  reviewComments: "",
});

const DateFieldInput = ({ name, value, canEdit, printMode, onChange, max }) => {
  if (printMode) {
    return (
      <div className={`${fieldClass} flex items-center`}>
        {displayDateUS(value) || "—"}
      </div>
    );
  }
  return (
    <InputDatePicker
      name={name}
      formatSeparator="/"
      allowManualEntry
      disabled={!canEdit}
      value={formatDateForInput(value) || ""}
      onChange={(event) => onChange(event.target.value)}
      max={max}
      className="w-full"
      inputClassName={`${fieldClass} bg-white`}
    />
  );
};

DateFieldInput.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  canEdit: PropTypes.bool,
  printMode: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  max: PropTypes.string,
};

const SelectField = ({ value, options, canEdit, onChange, placeholder }) => (
  <select
    className={fieldClass}
    value={value || ""}
    disabled={!canEdit}
    onChange={(e) => onChange(e.target.value)}
  >
    <option value="">{placeholder || "—"}</option>
    {options.map((opt) => (
      <option key={opt} value={opt}>
        {opt}
      </option>
    ))}
  </select>
);

SelectField.propTypes = {
  value: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  canEdit: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};

const normalizeMmtRows = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return emptyMmtRows();
  return rows.map((row) => ({
    id: row.id || emptyMmtRow().id,
    muscleGroup: row.muscleGroup || "",
    side: row.side || "",
    grade: row.grade || "",
  }));
};

const parsePain = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const rounded = Math.round(n);
  if (rounded < 0 || rounded > 10) return null;
  return rounded;
};

const PtVisitNoteForm = ({
  patient,
  clinicalForm,
  draft = null,
  onBack,
  onCreated,
  printMode = false,
  onReady,
}) => {
  const patientId = clinicalForm.patientId;
  const formId = clinicalForm.id || null;
  const isDraft = !formId;

  const [form, setForm] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const signatures = useFormSignatures();
  const { reset: resetSignatures } = signatures;

  const currentUser = useMemo(
    () => JSON.parse(localStorage.getItem("currentUser") || "{}"),
    [],
  );
  const role = resolveFormActorRole(currentUser);

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient?.lastName, patient?.firstName],
  );

  const buildPatientPrefill = useCallback(() => {
    return {
      ...emptyPtVisitPayload(),
    };
  }, []);

  const hydrate = useCallback(
    (data) => {
      setForm(data);
      const base = emptyPtVisitPayload();
      const loadedPayload =
        data.payload && typeof data.payload === "object" && !Array.isArray(data.payload)
          ? data.payload
          : {};

      const merged = {
        ...base,
        ...loadedPayload,
        ...pickPtVisitMeta(loadedPayload, data),
        visitDate: data.visitDate || loadedPayload.visitDate || today(),
        commonHeader: normalizePtCommonHeaderState(
          loadedPayload.commonHeader || base.commonHeader,
        ),
        mmtRows: normalizeMmtRows(loadedPayload.mmtRows),
        painPreVisit: parsePain(loadedPayload.painPreVisit),
        painPostVisit: parsePain(loadedPayload.painPostVisit),
        socDate: formatDateForInput(loadedPayload.socDate) || "",
        functionalImprovementSubjective:
          loadedPayload.functionalImprovementSubjective != null
            ? String(loadedPayload.functionalImprovementSubjective)
            : "",
        clinicianLicense:
          loadedPayload.clinicianLicense != null
            ? String(loadedPayload.clinicianLicense)
            : "",
        clinicianSignedDate:
          formatDateForInput(loadedPayload.clinicianSignedDate) || "",
        shortTermGoals: normalizeTextGoals(loadedPayload, {
          arrayKey: "shortTermGoals",
          legacyPrefix: "shortTermGoal",
          kind: "st",
          minCount: 1,
        }),
        longTermGoals: normalizeTextGoals(loadedPayload, {
          arrayKey: "longTermGoals",
          legacyPrefix: "longTermGoal",
          kind: "lt",
          minCount: 1,
        }),
        patientSignatureName:
          data.patientSignatureName || data.patientName || patientName,
        clinicianSignatureName: data.clinicianSignatureName || "",
        reviewComments: data.reviewComments || "",
      };

      PT_VISIT_ARRAY_KEYS.forEach((key) => {
        merged[key] = Array.isArray(loadedPayload[key])
          ? loadedPayload[key].map(String)
          : Array.isArray(base[key])
            ? base[key]
            : [];
      });

      // Drop legacy numbered goal keys from UI state once migrated to arrays.
      for (let n = 1; n <= 12; n += 1) {
        delete merged[`shortTermGoal${n}`];
        delete merged[`longTermGoal${n}`];
      }

      setValues(merged);
      resetSignatures();
    },
    [patientName, resetSignatures],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const finish = () => {
      if (cancelled) return;
      setLoading(false);
      onReady?.();
    };

    const load = async () => {
      try {
        if (isDraft) {
          const commonHeader = await resolvePtNotesCommonHeader({
            patient,
            patientId,
            payload: {},
          });
          if (cancelled) return;
          hydrate({
            formName:
              clinicalForm.formName ||
              draft?.formName ||
              "Physical Therapy Visit Note",
            formType: clinicalForm.formType || draft?.formType,
            formSection: clinicalForm.formSection || draft?.formSection,
            status: "pending",
            patientName,
            patientDateOfBirth: patient?.dateOfBirth || null,
            ...draftCaregiverIdentity(currentUser, role),
            createdAt: new Date().toISOString(),
            permissions: buildClinicalFormPermissions("pending", role),
            visitDate: today(),
            clinicianSignatureName: "",
            patientSignatureName: patientName,
            reviewComments: "",
            payload: { ...buildPatientPrefill(), commonHeader },
          });
          return;
        }

        const res = await getPtVisitNoteForm(patientId, formId);
        if (cancelled) return;
        const loaded = res.result?.form || res.form;
        const existingPayload =
          loaded.payload && typeof loaded.payload === "object"
            ? loaded.payload
            : {};
        const commonHeader = await resolvePtNotesCommonHeader({
          patient,
          patientId,
          payload: existingPayload,
        });
        if (cancelled) return;
        hydrate({
          ...loaded,
          payload: { ...existingPayload, commonHeader },
        });
      } catch (err) {
        if (cancelled) return;
        toast.error(err.message || "Failed to load form");
        onBack();
      } finally {
        finish();
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, formId, isDraft]);

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const toggleListValue = (key, item) => {
    setValues((prev) => ({
      ...prev,
      [key]: toggleInList(prev[key], item),
    }));
  };

  const updateMmtRow = (rowId, patch) => {
    setValues((prev) => ({
      ...prev,
      mmtRows: (prev.mmtRows || []).map((row) =>
        row.id === rowId ? { ...row, ...patch } : row,
      ),
    }));
  };

  const addMmtRow = () => {
    setValues((prev) => ({
      ...prev,
      mmtRows: [...(prev.mmtRows || []), emptyMmtRow()],
    }));
  };

  const removeMmtRow = (rowId) => {
    setValues((prev) => {
      const next = (prev.mmtRows || []).filter((row) => row.id !== rowId);
      return { ...prev, mmtRows: next.length ? next : emptyMmtRows() };
    });
  };

  const updateTextGoal = (listKey, goalId, text) => {
    setValues((prev) => ({
      ...prev,
      [listKey]: (prev[listKey] || []).map((row) =>
        row.id === goalId ? { ...row, text } : row,
      ),
    }));
  };

  const addTextGoal = (listKey, kind) => {
    setValues((prev) => {
      const current = prev[listKey] || [];
      return {
        ...prev,
        [listKey]: renumberTextGoals(
          [...current, emptyTextGoalRow(current.length + 1, kind)],
          kind,
        ),
      };
    });
  };

  const removeTextGoal = (listKey, kind, goalId) => {
    setValues((prev) => {
      const current = prev[listKey] || [];
      if (current.length <= 1) return prev;
      return {
        ...prev,
        [listKey]: renumberTextGoals(
          current.filter((row) => row.id !== goalId),
          kind,
        ),
      };
    });
  };

  const { permissions: basePermissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId: currentUser?.id ? Number(currentUser.id) : null,
    formFilledByUserId:
      form?.filledBy ||
      clinicalForm?.filledBy ||
      (currentUser?.id ? Number(currentUser.id) : null),
    formFilledByRole: form?.filledByRole || clinicalForm?.filledByRole || role,
    serverPermissions: form?.permissions,
  });

  const permissions = useMemo(
    () =>
      applyDisciplineFormPermissions(
        basePermissions,
        role,
        PT_FORM_EDITOR_ROLES,
        "View only — only PT/PTA can edit this form.",
      ),
    [basePermissions, role],
  );

  const canEdit = !printMode && Boolean(permissions.canEdit);
  const isReviewer = Boolean(permissions.canReview);
  const canSign = !printMode && Boolean(permissions.canSignSignatures);

  const {
    caregiverSlot,
    filledByUserId,
    assertCaregiverForCreate,
  } = useFormCaregiverPicker({
    patientId,
    formType: form?.formType || draft?.formType || clinicalForm?.formType,
    role,
    canEdit,
    preferredStaffId: draft?.visit?.staffId ?? null,
    currentFilledByUserId:
      form?.filledBy || clinicalForm?.filledBy || null,
  });

  const handleClinicianSign = (signature, signerName) => {
    signatures.handleClinicianSign(signature);
    setValues((prev) => ({
      ...prev,
      clinicianSignatureName: signerName || prev.clinicianSignatureName,
    }));
  };

  const buildPayload = (flag) => {
    const {
      clinicianSignatureName,
      patientSignatureName,
      reviewComments,
      visitDate,
      ...ptFields
    } = values;

    const nestedPayload = withNormalizedPtCommonHeader({
      ...ptFields,
      visitDate: visitDate ? formatDateForInput(visitDate) : "",
      socDate: values.socDate ? formatDateForInput(values.socDate) : "",
      nextVisitDate: values.nextVisitDate
        ? formatDateForInput(values.nextVisitDate)
        : "",
      clinicianSignedDate: values.clinicianSignedDate
        ? formatDateForInput(values.clinicianSignedDate)
        : "",
      supervisoryDate: values.supervisoryDate
        ? formatDateForInput(values.supervisoryDate)
        : "",
      functionalImprovementSubjective:
        values.functionalImprovementSubjective || "",
      clinicianLicense: values.clinicianLicense || "",
      mmtRows: normalizeMmtRows(values.mmtRows),
      painPreVisit: parsePain(values.painPreVisit),
      painPostVisit: parsePain(values.painPostVisit),
      shortTermGoals: serializeTextGoals(values.shortTermGoals),
      longTermGoals: serializeTextGoals(values.longTermGoals),
    });

    PT_VISIT_ARRAY_KEYS.forEach((key) => {
      nestedPayload[key] = Array.isArray(values[key]) ? values[key] : [];
    });

    for (let n = 1; n <= 12; n += 1) {
      delete nestedPayload[`shortTermGoal${n}`];
      delete nestedPayload[`longTermGoal${n}`];
    }

    const payload = {
      flag,
      visitDate: visitDate ? formatDateForInput(visitDate) : null,
      billingCode: values.billingCode || "",
      timeIn: values.timeIn || "",
      timeOut: values.timeOut || "",
      payload: nestedPayload,
      clinicianSignatureName: clinicianSignatureName || "",
      patientSignatureName: patientSignatureName || "",
      reviewComments: reviewComments || "",
    };

    if (!canSign) return payload;
    return { ...payload, ...signatures.buildSignaturePayload() };
  };

  const submit = async (flag) => {
    if (flag === "to_be_corrected" && !values.reviewComments?.trim()) {
      toast.error("Add review comments before marking the form to be corrected");
      return;
    }
    const visitMetaError = assertPtVisitMetaForFlag(values, flag);
    if (visitMetaError) {
      toast.error(visitMetaError);
      return;
    }
    const caregiverError = assertCaregiverForCreate();
    if (caregiverError) {
      toast.error(caregiverError);
      return;
    }
    setSaving(flag);
    try {
      const { form: saved, createdClinicalFormId } = await saveDedicatedForm({
        patientId,
        clinicalForm,
        draft,
        flag,
        payload: buildPayload(flag),
        createClinicalForm: createPatientClinicalForm,
        updateDedicatedForm: updatePtVisitNoteForm,
        filledByUserId,
        scheduleDate: formatDateForInput(values.visitDate) || today(),
      });
      hydrate(saved);
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
      if (createdClinicalFormId) onCreated?.(createdClinicalFormId);
    } catch (err) {
      toast.error(err.message || "Failed to save form");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <FormLoadingSkeleton />;
  if (!form) return null;

  const signatureProps = signatures.sectionProps(form);
  const showReasonOther = (values.reasonForVisit || []).includes("Other");
  const showInterventionOther = (values.interventions || []).includes("Other");

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {!printMode && (
        <FormPageHeader
          patientName={patientName}
          mrn={patient?.mrn}
          status={form.status}
          onBack={onBack}
        />
      )}

      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            Physical Therapy Visit Note
          </h1>
          <p className="mt-1 text-[11px] text-slate-500">Physical Therapy</p>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        <FormIdentityStrip
          form={form}
          patientName={patientName || form.patientName}
          mrn={patient?.mrn}
          dateOfBirth={displayDateUS(
            patient?.dateOfBirth ||
              values.commonHeader?.dateOfBirth ||
              form.patientDateOfBirth,
          )}
          caregiverSlot={caregiverSlot}
          dateLabel="Date of Visit"
          dateValue={displayDateUS(values.visitDate)}
          dateSlot={
            printMode ? null : (
              <div className="min-w-0">
                <div className="text-[7px] font-semibold uppercase tracking-wider text-slate-400">
                  Date of Visit
                </div>
                <InputDatePicker
                  name="visitDate"
                  formatSeparator="/"
                  allowManualEntry
                  compact
                  disabled={!canEdit}
                  value={formatDateForInput(values.visitDate) || ""}
                  onChange={(event) => setValue("visitDate", event.target.value)}
                  max={VISIT_DATE_MAX}
                  className="mt-0.5 min-w-0"
                  inputClassName="w-full h-4 text-[10px] font-bold bg-transparent border-0 p-0 outline-none"
                />
              </div>
            )
          }
          visitMeta={buildPtVisitMetaProps({
            values,
            setValue,
            setValues,
            canEdit,
            printMode,
          })}
        />

        <FormMetaRow form={form} printMode={printMode} />
        {!printMode && <LockedReasonBanner reason={permissions.lockedReason} />}

        {/* Fixed sections 1–6 */}
        <PtNotesCommonHeader
          value={values.commonHeader}
          canEdit={canEdit}
          printMode={printMode}
          onChange={(next) => setValue("commonHeader", next)}
        />

        {/* 7. Reason for Visit / Since Last Visit */}
        <Section
          icon={CommentsIcon}
          title="7. Reason for Visit / Since Last Visit"
        >
          <SubHeading>Reason for Visit</SubHeading>
          <CheckboxGrid
            options={REASON_FOR_VISIT_OPTIONS}
            values={values.reasonForVisit}
            canEdit={canEdit}
            onToggle={(opt) => toggleListValue("reasonForVisit", opt)}
            columns={3}
          />
          {showReasonOther ? (
            <input
              type="text"
              className={`${fieldClass} mt-1.5`}
              value={values.reasonForVisitOther || ""}
              disabled={!canEdit}
              placeholder="Specify other reason…"
              onChange={(e) => setValue("reasonForVisitOther", e.target.value)}
            />
          ) : null}
          <div className="mt-3">
            <Field label="Since Last Visit (Patient/Caregiver Report)">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.sinceLastVisit || ""}
                disabled={!canEdit}
                placeholder="Describe status change, compliance, or concerns…"
                onChange={(e) => setValue("sinceLastVisit", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        {/* 8. Subjective */}
        <Section icon={HelpingIcon} title="8. Subjective">
          <Field label="Patient Report (Pain, Symptoms, Functional Changes)">
            <textarea
              rows={3}
              className={textareaClass}
              value={values.patientReport || ""}
              disabled={!canEdit}
              onChange={(e) => setValue("patientReport", e.target.value)}
            />
          </Field>
          <div className="mt-3">
            <SubHeading>Functional Improvement</SubHeading>
            <ExclusiveChoiceGrid
              name="functionalImprovementSubjective"
              options={YES_NO_SOME_OPTIONS}
              value={values.functionalImprovementSubjective || ""}
              canEdit={canEdit}
              onChange={(opt) =>
                setValue("functionalImprovementSubjective", opt)
              }
              columns={3}
            />
          </div>
        </Section>

        {/* Pain scales */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <PainScalePills
            label="Pain Scale (0–10): Pre-Visit"
            name="painPreVisit"
            value={values.painPreVisit}
            canEdit={canEdit}
            onChange={(v) => setValue("painPreVisit", v)}
          />
          <PainScalePills
            label="Pain Scale (0–10): Post-Visit"
            name="painPostVisit"
            value={values.painPostVisit}
            canEdit={canEdit}
            onChange={(v) => setValue("painPostVisit", v)}
          />
        </div>

        {/* 9. Objective */}
        <Section
          icon={RunningIcon}
          title="9. Objective – Measurements & Functional Assessment"
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            {/* Left: posture + ROM */}
            <div className="space-y-3 min-w-0">
              <Field label="Posture / Observation">
                <textarea
                  rows={5}
                  className={`${textareaClass} min-h-[7.5rem]`}
                  value={values.postureObservation || ""}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("postureObservation", e.target.value)
                  }
                />
              </Field>
              <div className="rounded-md border border-slate-200 p-2.5">
                <SubHeading>Range of Motion (if applicable)</SubHeading>
                <div className="mt-2 space-y-2.5">
                  {[
                    ["Knee Ext", "romKneeExtRight", "romKneeExtLeft"],
                    ["Knee Flex", "romKneeFlexRight", "romKneeFlexLeft"],
                  ].map(([label, rightKey, leftKey]) => (
                    <div
                      key={label}
                      className="grid grid-cols-1 sm:grid-cols-[5.5rem_1fr_1fr] gap-2 items-end"
                    >
                      <div className="text-[11px] font-semibold text-slate-600 sm:pb-1.5">
                        {label}
                      </div>
                      <Field label="Right (R)">
                        <input
                          type="text"
                          className={`${fieldClass} tabular-nums`}
                          value={values[rightKey] || ""}
                          disabled={!canEdit}
                          onChange={(e) => setValue(rightKey, e.target.value)}
                        />
                      </Field>
                      <Field label="Left (L)">
                        <input
                          type="text"
                          className={`${fieldClass} tabular-nums`}
                          value={values[leftKey] || ""}
                          disabled={!canEdit}
                          onChange={(e) => setValue(leftKey, e.target.value)}
                        />
                      </Field>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Middle: MMT + assistive */}
            <div className="space-y-3 min-w-0">
              <div className="rounded-md border border-slate-200 overflow-hidden min-w-0">
                <div className="px-2.5 py-1.5 border-b border-slate-200 bg-slate-50 flex items-center justify-between gap-2">
                  <SubHeading>Strength (MMT)</SubHeading>
                  {canEdit ? (
                    <button
                      type="button"
                      className="text-[10px] font-semibold text-indigo-600 hover:text-indigo-800 shrink-0"
                      onClick={addMmtRow}
                    >
                      + Add row
                    </button>
                  ) : null}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full table-fixed text-left text-[11px]">
                    <colgroup>
                      <col className="w-[42%]" />
                      <col className="w-[22%]" />
                      <col className="w-[26%]" />
                      {canEdit ? <col className="w-[10%]" /> : null}
                    </colgroup>
                    <thead>
                      <tr className="bg-slate-50/80 text-[9px] font-semibold uppercase tracking-wide text-slate-500">
                        <th className="px-2 py-1.5">Muscle Group</th>
                        <th className="px-2 py-1.5">R/L</th>
                        <th className="px-2 py-1.5">Grade</th>
                        {canEdit ? <th className="px-1 py-1.5" /> : null}
                      </tr>
                    </thead>
                    <tbody>
                      {(values.mmtRows || []).map((row) => (
                        <tr key={row.id} className="border-t border-slate-100">
                          <td className="px-1.5 py-1 min-w-0">
                            <input
                              type="text"
                              className={`${fieldClass} min-w-0`}
                              value={row.muscleGroup || ""}
                              disabled={!canEdit}
                              onChange={(e) =>
                                updateMmtRow(row.id, {
                                  muscleGroup: e.target.value,
                                })
                              }
                            />
                          </td>
                          <td className="px-1.5 py-1 min-w-0">
                            <select
                              className={`${fieldClass} min-w-0`}
                              value={row.side || ""}
                              disabled={!canEdit}
                              onChange={(e) =>
                                updateMmtRow(row.id, { side: e.target.value })
                              }
                            >
                              <option value="">—</option>
                              {MMT_SIDE_OPTIONS.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-1.5 py-1 min-w-0">
                            <input
                              type="text"
                              className={`${fieldClass} min-w-0 tabular-nums`}
                              value={row.grade || ""}
                              disabled={!canEdit}
                              placeholder="4/5"
                              onChange={(e) =>
                                updateMmtRow(row.id, { grade: e.target.value })
                              }
                            />
                          </td>
                          {canEdit ? (
                            <td className="px-0.5 py-1 text-center">
                              <button
                                type="button"
                                className="inline-flex h-6 w-6 items-center justify-center rounded text-rose-500 hover:bg-rose-50 hover:text-rose-700"
                                onClick={() => removeMmtRow(row.id)}
                                aria-label="Remove MMT row"
                              >
                                ×
                              </button>
                            </td>
                          ) : null}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <Field label="Assistive Device">
                <SelectField
                  value={values.assistiveDevice}
                  options={ASSISTIVE_DEVICE_OPTIONS}
                  canEdit={canEdit}
                  onChange={(v) => setValue("assistiveDevice", v)}
                />
              </Field>
            </div>

            {/* Right: balance + gait + functional tests */}
            <div className="space-y-3 min-w-0">
              <div>
                <SubHeading>Balance</SubHeading>
                <ExclusiveChoiceGrid
                  name="balance"
                  options={BALANCE_OPTIONS}
                  value={values.balance || ""}
                  canEdit={canEdit}
                  onChange={(opt) => setValue("balance", opt)}
                  columns={3}
                />
              </div>
              <div>
                <SubHeading>Gait / Mobility</SubHeading>
                <CheckboxGrid
                  options={GAIT_MOBILITY_OPTIONS}
                  values={values.gaitMobility}
                  canEdit={canEdit}
                  onToggle={(opt) => toggleListValue("gaitMobility", opt)}
                  columns={2}
                />
              </div>
              <Field label="Functional Tests (e.g. TUG)">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.functionalTests || ""}
                  disabled={!canEdit}
                  placeholder="TUG: 14.2s (Score)"
                  onChange={(e) => setValue("functionalTests", e.target.value)}
                />
              </Field>
            </div>
          </div>
        </Section>

        {/* 10. Interventions Provided */}
        <Section icon={HeartPulseIcon} title="10. Interventions Provided">
          <CheckboxGrid
            options={PT_VISIT_INTERVENTION_OPTIONS}
            values={values.interventions}
            canEdit={canEdit}
            onToggle={(opt) => toggleListValue("interventions", opt)}
            columns={3}
          />
          {showInterventionOther ? (
            <input
              type="text"
              className={`${fieldClass} mt-1.5`}
              value={values.interventionOther || ""}
              disabled={!canEdit}
              placeholder="Other intervention…"
              onChange={(e) => setValue("interventionOther", e.target.value)}
            />
          ) : null}
          <div className="mt-3">
            <Field label="Details">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.interventionDetails || ""}
                disabled={!canEdit}
                placeholder="Quad sets, SLR, terminal knee extension 2×10…"
                onChange={(e) =>
                  setValue("interventionDetails", e.target.value)
                }
              />
            </Field>
          </div>
        </Section>

        {/* 11. Patient Response / Progress */}
        <Section icon={TargetIcon} title="11. Patient Response / Progress">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="min-w-0">
              <SubHeading>Patient Response to Treatment</SubHeading>
              <ExclusiveChoiceGrid
                name="patientResponse"
                options={PATIENT_RESPONSE_OPTIONS}
                value={values.patientResponse || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("patientResponse", opt)}
                columns={3}
              />
            </div>
            <div className="min-w-0">
              <SubHeading>Progress Toward Goals</SubHeading>
              <ExclusiveChoiceGrid
                name="progressTowardGoals"
                options={PROGRESS_TOWARD_GOALS_OPTIONS}
                value={values.progressTowardGoals || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("progressTowardGoals", opt)}
                columns={2}
              />
            </div>
            <div className="min-w-0">
              <SubHeading>Functional Improvement</SubHeading>
              <ExclusiveChoiceGrid
                name="functionalImprovementProgress"
                options={YES_NO_SOME_OPTIONS}
                value={values.functionalImprovementProgress || ""}
                canEdit={canEdit}
                onChange={(opt) =>
                  setValue("functionalImprovementProgress", opt)
                }
                columns={3}
              />
            </div>
          </div>
          <div className="mt-3">
            <Field label="Comments">
              <textarea
                rows={2}
                className={textareaClass}
                value={values.responseComments || ""}
                disabled={!canEdit}
                onChange={(e) => setValue("responseComments", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        {/* 12. Current Functional Status */}
        <Section icon={RunningIcon} title="12. Current Functional Status">
          <div className="space-y-3">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              <div className="min-w-0">
                <SubHeading>Mobility</SubHeading>
                <ExclusiveChoiceGrid
                  name="mobilityTrend"
                  options={MOBILITY_TREND_OPTIONS}
                  value={values.mobilityTrend || ""}
                  canEdit={canEdit}
                  onChange={(opt) => setValue("mobilityTrend", opt)}
                  columns={3}
                />
              </div>
              <Field label="Ambulation">
                <SelectField
                  value={values.ambulationStatus}
                  options={AMBULATION_STATUS_OPTIONS}
                  canEdit={canEdit}
                  onChange={(v) => setValue("ambulationStatus", v)}
                />
              </Field>
              <div className="min-w-0">
                <SubHeading>Home Safety</SubHeading>
                <CheckboxGrid
                  options={HOME_SAFETY_OPTIONS}
                  values={values.homeSafety}
                  canEdit={canEdit}
                  onToggle={(opt) => toggleListValue("homeSafety", opt)}
                  columns={1}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <Field label="Transfers">
                <SelectField
                  value={values.transfersStatus}
                  options={TRANSFER_STATUS_OPTIONS}
                  canEdit={canEdit}
                  onChange={(v) => setValue("transfersStatus", v)}
                />
              </Field>
              <div className="min-w-0">
                <SubHeading>ADLs Impacted</SubHeading>
                <CheckboxGrid
                  options={ADL_IMPACTED_OPTIONS}
                  values={values.adlsImpacted}
                  canEdit={canEdit}
                  onToggle={(opt) => toggleListValue("adlsImpacted", opt)}
                  columns={2}
                />
              </div>
            </div>
          </div>
        </Section>

        {/* 13. Goals */}
        <Section icon={TargetIcon} title="13. Goals">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-md border border-slate-200 bg-slate-50/40 p-3 space-y-2.5 min-w-0">
              <div>
                <SubHeading>Short-Term Goals (1–4 weeks)</SubHeading>
                <p className="text-[10px] text-slate-400 mt-0.5">
                  Measurable objectives expected within 1–4 weeks.
                </p>
              </div>
              {(values.shortTermGoals || []).map((row, idx) => (
                <div key={row.id} className="flex gap-2 min-w-0">
                  <span
                    className="mt-1.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-[10px] font-bold text-indigo-700"
                    aria-hidden
                  >
                    {idx + 1}
                  </span>
                  <textarea
                    rows={2}
                    className={`${textareaClass} min-w-0 flex-1`}
                    value={row.text || ""}
                    disabled={!canEdit}
                    placeholder={`Short-term goal ${idx + 1}…`}
                    aria-label={`Short-term goal ${idx + 1}`}
                    onChange={(e) =>
                      updateTextGoal("shortTermGoals", row.id, e.target.value)
                    }
                  />
                  {canEdit ? (
                    <button
                      type="button"
                      disabled={(values.shortTermGoals || []).length <= 1}
                      onClick={() =>
                        removeTextGoal("shortTermGoals", "st", row.id)
                      }
                      className="mt-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-30"
                      aria-label={`Remove short-term goal ${idx + 1}`}
                    >
                      <DeleteIcon size={14} />
                    </button>
                  ) : null}
                </div>
              ))}
              {canEdit ? (
                <button
                  type="button"
                  onClick={() => addTextGoal("shortTermGoals", "st")}
                  className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold rounded-md border border-slate-200 text-slate-700 hover:border-indigo-300 hover:bg-white"
                >
                  <PlusIcon size={13} />
                  Add Goal
                </button>
              ) : null}
            </div>

            <div className="rounded-md border border-slate-200 bg-slate-50/40 p-3 space-y-2.5 min-w-0">
              <div>
                <SubHeading>Long-Term Goals (4+ weeks)</SubHeading>
                <p className="text-[10px] text-slate-400 mt-0.5">
                  Discharge-level outcomes expected at 4+ weeks.
                </p>
              </div>
              {(values.longTermGoals || []).map((row, idx) => (
                <div key={row.id} className="flex gap-2 min-w-0">
                  <span
                    className="mt-1.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[10px] font-bold text-slate-700"
                    aria-hidden
                  >
                    {idx + 1}
                  </span>
                  <textarea
                    rows={2}
                    className={`${textareaClass} min-w-0 flex-1`}
                    value={row.text || ""}
                    disabled={!canEdit}
                    placeholder={`Long-term goal ${idx + 1}…`}
                    aria-label={`Long-term goal ${idx + 1}`}
                    onChange={(e) =>
                      updateTextGoal("longTermGoals", row.id, e.target.value)
                    }
                  />
                  {canEdit ? (
                    <button
                      type="button"
                      disabled={(values.longTermGoals || []).length <= 1}
                      onClick={() =>
                        removeTextGoal("longTermGoals", "lt", row.id)
                      }
                      className="mt-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-30"
                      aria-label={`Remove long-term goal ${idx + 1}`}
                    >
                      <DeleteIcon size={14} />
                    </button>
                  ) : null}
                </div>
              ))}
              {canEdit ? (
                <button
                  type="button"
                  onClick={() => addTextGoal("longTermGoals", "lt")}
                  className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold rounded-md border border-slate-200 text-slate-700 hover:border-indigo-300 hover:bg-white"
                >
                  <PlusIcon size={13} />
                  Add Goal
                </button>
              ) : null}
            </div>
          </div>
        </Section>

        {/* 14. Plan / Treatment Plan */}
        <Section icon={FileCheckIcon} title="14. Plan / Treatment Plan">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="min-w-0">
              <SubHeading>Continue PT?</SubHeading>
              <ExclusiveChoiceGrid
                name="continuePt"
                options={YES_NO_OPTIONS}
                value={values.continuePt || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("continuePt", opt)}
                columns={2}
              />
            </div>
            <Field label="Frequency">
              <input
                type="text"
                className={fieldClass}
                value={values.frequency || ""}
                disabled={!canEdit}
                placeholder="e.g., 2x / week"
                onChange={(e) => setValue("frequency", e.target.value)}
              />
            </Field>
            <Field label="Next Visit">
              <DateFieldInput
                name="nextVisitDate"
                value={values.nextVisitDate}
                canEdit={canEdit}
                printMode={printMode}
                onChange={(v) => setValue("nextVisitDate", v)}
              />
            </Field>
          </div>
          <div className="mt-3">
            <Field label="Specific Interventions Planned">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.interventionsPlanned || ""}
                disabled={!canEdit}
                placeholder="Progress HEP, gait on uneven surfaces…"
                onChange={(e) =>
                  setValue("interventionsPlanned", e.target.value)
                }
              />
            </Field>
          </div>
        </Section>

        {/* 15. Additional Notes */}
        <Section icon={CommentsIcon} title="15. Additional Notes">
          <textarea
            rows={4}
            className={textareaClass}
            value={values.additionalNotes || ""}
            disabled={!canEdit}
            placeholder="Enter any additional clinical observations, caregiver communication, or coordination of care details…"
            onChange={(e) => setValue("additionalNotes", e.target.value)}
          />
        </Section>

        {/* Full-width rows: Supervisory → Patient → PT/PTA */}
        <div className="space-y-3">
          <Section icon={FileCheckIcon} title="Supervisory Review (If Applicable)">
            <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1.2fr)_minmax(0,1.4fr)_minmax(0,1fr)] gap-3 items-end">
              <Field label="Reviewer Name">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.supervisoryReviewerName || ""}
                  disabled={!canEdit}
                  placeholder="Clinical Supervisor Name"
                  onChange={(e) =>
                    setValue("supervisoryReviewerName", e.target.value)
                  }
                />
              </Field>
              <Field label="Functional Improvement">
                <div
                  className="flex flex-nowrap gap-1.5"
                  role="radiogroup"
                  aria-label="supervisoryFunctionalImprovement"
                >
                  {YES_NO_SOME_OPTIONS.map((opt) => {
                    const checked =
                      (values.supervisoryFunctionalImprovement || "") === opt;
                    return (
                      <label
                        key={opt}
                        className={`inline-flex min-w-0 flex-1 items-center justify-center gap-1.5 text-[12px] text-slate-700 rounded-md border px-2 py-1.5 ${
                          canEdit ? "cursor-pointer" : "cursor-default"
                        } ${
                          checked
                            ? "border-indigo-300 bg-indigo-50/60"
                            : "border-slate-200 bg-white"
                        }`}
                      >
                        <input
                          type="radio"
                          name="supervisoryFunctionalImprovement"
                          className={`${checkboxClass} mt-0 shrink-0`}
                          checked={checked}
                          disabled={!canEdit}
                          onChange={() => {
                            if (!canEdit) return;
                            setValue("supervisoryFunctionalImprovement", opt);
                          }}
                          onClick={(e) => {
                            if (canEdit && checked) {
                              e.preventDefault();
                              setValue("supervisoryFunctionalImprovement", "");
                            }
                          }}
                        />
                        <span className="leading-snug whitespace-nowrap">
                          {opt}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </Field>
              <Field label="Date">
                <DateFieldInput
                  name="supervisoryDate"
                  value={values.supervisoryDate}
                  canEdit={canEdit}
                  printMode={printMode}
                  onChange={(v) => setValue("supervisoryDate", v)}
                />
              </Field>
            </div>
          </Section>

          <FormSignatureSections
            {...signatureProps}
            showPatientSection
            patientNameValue={values.patientSignatureName}
            onPatientNameChange={(v) => setValue("patientSignatureName", v)}
            clinicianNameValue={values.clinicianSignatureName}
            onClinicianSign={handleClinicianSign}
            canSign={canSign}
            showClinicianSection={!permissions.hideClinicalSignature}
            printMode={printMode}
            clinicianSectionTitle="PT / PTA Authentication"
            patientSectionTitle="Patient / Caregiver (Acknowledgment of Visit)"
          />
        </div>

        {isReviewer && !printMode && (
          <OfficeReviewSection
            value={values.reviewComments}
            onChange={(v) => setValue("reviewComments", v)}
          />
        )}

        {(!isReviewer || printMode) && (
          <OfficeCommentsNote comments={form.reviewComments} />
        )}
      </div>

      {(permissions.canSendToOffice ||
        permissions.canToBeSign ||
        permissions.canReview ||
        permissions.canEdit) && (
        <FormActionBar
          permissions={permissions}
          status={form.status}
          canEdit={canEdit}
          saving={saving}
          onSubmit={submit}
          printMode={printMode}
        />
      )}
    </div>
  );
};

PtVisitNoteForm.propTypes = {
  patient: PropTypes.object,
  clinicalForm: PropTypes.object.isRequired,
  draft: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
  printMode: PropTypes.bool,
  onReady: PropTypes.func,
};

export default PtVisitNoteForm;
